/**
 * Read adapters for stored rating-likert slides (plots plan P; contract in
 * `../contract.ts`, shapes in docs/deck-design-system/plots/read-adapters.md).
 *
 * - A slide built with `plot` (Python's diverging-likert payload) is read as
 *   stored: bands from `rating_segments.py`, net, the NPS score and the legend
 *   all stamped.
 * - A slide stored before `plot` is LEGACY, with or without `segments`: it
 *   carries no stamped `net`, NPS score or legend, which a shape-only adapter
 *   cannot supply, so today's resolver (`legacy/rating-likert-split.ts`, moved
 *   unchanged) draws it. Its one change is the legend of a multi-row slide
 *   whose pooled parent paints nothing (lane-merged): the rows' bands.
 *
 * Stored shapes: `tests/fixtures/plots/diverging-likert-*.stored.v1.json`.
 */

import { clampArray } from '../../foundations/slide-limits'
import type { RatingLikertSlideData } from '../../slides/renderer-internal-types'
import type { PlotReadAdapter, StoredPlotPayload } from '../contract'
import type { RatingBarLegendEntry } from './bar-plan'
import { resolveRatingBar } from './legacy/rating-likert-split'
import type { RatingBarResolution } from './legacy/rating-likert-split'
import { divergingLikertLegend, planDivergingLikertRow, unionLegend } from './plot-plan'
import type { DivergingLikertDrawnBar, DivergingLikertStoredData } from './plot-plan'
import { divergingLikertActiveView } from './stat-view-plan'

const SLIDE_TYPE = 'rating-likert' as const

/** The deck `rating-likert` slide as stored, plot optional. */
export type StoredRatingLikertSlide = RatingLikertSlideData & {
  plot?: { kind: string; data: unknown } | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function hasStampedPlot(slide: StoredRatingLikertSlide): boolean {
  const plot = slide.plot
  return isRecord(plot) && plot.kind === 'diverging-likert' && isRecord(plot.data)
    && Array.isArray(plot.data.rows) && Array.isArray(plot.data.legend)
}

/**
 * A stamped diverging-likert payload's data, or null. The deck segment views
 * (W6-G) read a tab's `plot` with it: a live view, never stored.
 */
export function readStampedDivergingLikert(plot: unknown): DivergingLikertStoredData | null {
  if (!isRecord(plot) || plot.kind !== 'diverging-likert' || !isRecord(plot.data)) {
    return null
  }
  const data = plot.data
  if (!Array.isArray(data.rows) || !Array.isArray(data.legend)) {
    return null
  }
  return data as unknown as DivergingLikertStoredData
}

function isRatingLikertSlide(stored: unknown): stored is StoredRatingLikertSlide {
  return isRecord(stored) && stored.type === SLIDE_TYPE && isRecord(stored.scale) && Array.isArray(stored.buckets)
}

/** A slide built with `plot`: the stamped payload, as stored. */
export const deckSlideDivergingLikertAdapter: PlotReadAdapter<'diverging-likert', StoredRatingLikertSlide> = {
  kind: 'diverging-likert',
  source: { surface: 'deck-slide', shape: SLIDE_TYPE },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredRatingLikertSlide {
    return isRatingLikertSlide(stored) && hasStampedPlot(stored)
  },
  read(stored: StoredRatingLikertSlide): StoredPlotPayload<'diverging-likert'> {
    return stored.plot as unknown as StoredPlotPayload<'diverging-likert'>
  },
}

/** Today's resolution of every drawn bar of a stored slide without `plot`. */
export interface LegacyRatingLikertRead {
  /** The pooled bar, or the one row's bar when the slide has one row. */
  bar: RatingBarResolution
  rows: Array<{ id: string; label: string; imageUrl?: string | null; bar: RatingBarResolution }>
  legend: Array<RatingBarLegendEntry>
}

/** Today's reader: the rows the renderer draws, the pooled bar and its legend. */
export function resolveLegacyRatingLikert(slide: RatingLikertSlideData): LegacyRatingLikertRead {
  const rows = clampArray(slide.rows || [], SLIDE_TYPE, 'rows').map((row) => ({
    id: row.id,
    label: row.label,
    imageUrl: row.imageUrl,
    bar: resolveRatingBar(
      {
        segments: row.segments,
        buckets: clampArray(row.buckets || [], SLIDE_TYPE, 'buckets'),
        scale: slide.scale,
        ratingStatistics: row.ratingStatistics,
      },
      slide.displayLabels,
    ),
  }))
  // With one explicit row, the headline statistics describe that same row.
  // Multi-row decks retain their supplied aggregate summary.
  const bar = rows.length === 1
    ? rows[0].bar
    : resolveRatingBar(
      {
        segments: slide.segments,
        buckets: clampArray(slide.buckets || [], SLIDE_TYPE, 'buckets'),
        scale: slide.scale,
        ratingStatistics: slide.ratingStatistics,
      },
      slide.displayLabels,
    )
  const parentLegend = bar.plan?.legend || []
  // Lane-merged fix: a multi-row slide whose pooled parent paints nothing
  // (no pooled breakdown exists) names the bands its rows paint.
  const legend = parentLegend.length || rows.length < 2
    ? parentLegend
    : unionLegend(rows.map((row) => row.bar.plan))
  return { bar, rows, legend }
}

/** A slide stored before `plot` (with or without `segments`): today's resolver. */
export const legacyDeckSlideDivergingLikertAdapter: PlotReadAdapter<'diverging-likert', StoredRatingLikertSlide> = {
  kind: 'diverging-likert',
  source: { surface: 'deck-slide', shape: SLIDE_TYPE },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredRatingLikertSlide {
    return isRatingLikertSlide(stored) && !hasStampedPlot(stored)
  },
  read(stored: StoredRatingLikertSlide): StoredPlotPayload<'diverging-likert'> {
    const read = resolveLegacyRatingLikert(stored)
    const drawn = read.rows.length ? read.rows : [{ id: 'total', label: '', imageUrl: null, bar: read.bar }]
    const unsupported = [read.bar, ...read.rows.map((row) => row.bar)]
      .some((bar) => bar.dataStatus === 'unsupported')
    const labels = stored.displayLabels ?? {}
    return {
      kind: 'diverging-likert',
      data: {
        status: unsupported ? 'unsupported' : 'ok',
        stat: stored.scale.kind === 'nps' ? 'nps' : 'favorable_share',
        legend: read.legend.map((entry) => ({ band: entry.band, label: entry.label })),
        rows: unsupported ? [] : drawn.map((row) => ({
          id: row.id,
          label: row.label,
          imageUrl: row.imageUrl ?? null,
          segments: (row.bar.plan?.pieces ?? []).map((piece) => ({
            label: piece.label,
            value: piece.value,
            band: piece.band,
          })),
          bands: {
            favorable: row.bar.split.metricValues[0] ?? 0,
            middle: row.bar.split.metricValues[1] ?? 0,
            unfavorable: row.bar.split.metricValues[2] ?? 0,
          },
          base: row.bar === read.bar
            ? stored.ratingStatistics?.baseSize ?? null
            : stored.rows?.find((candidate) => candidate.id === row.id)?.ratingStatistics?.baseSize ?? null,
          lowBase: false,
          net: row.bar.plan?.net ?? null,
        })),
        // Today's slide prints whole percents.
        decimals: 0,
        labels: {
          ...(labels.favorable ? { favorable: labels.favorable } : {}),
          ...(labels.neutral ? { neutral: labels.neutral } : {}),
          ...(labels.unfavorable ? { unfavorable: labels.unfavorable } : {}),
          ...(labels.net ? { net: labels.net } : {}),
          ...(labels.mixed ? { mixed_band: labels.mixed } : {}),
          ...(labels.unsupportedScale ? { unsupported_rating_scale: labels.unsupportedScale } : {}),
        },
      },
    }
  },
}

/** A quick-study result as stored, `diverging_likert_plot` optional (W6-QS). */
export interface StoredQuickStudyLikert {
  diverging_likert_plot?: unknown
}

function quickStudyLikertPlotOf(plot: unknown): StoredPlotPayload<'diverging-likert'> | null {
  if (typeof plot !== 'object' || plot === null) {
    return null
  }
  const payload = plot as { kind?: unknown; data?: { rows?: unknown } }
  if (payload.kind !== 'diverging-likert' || typeof payload.data !== 'object' || payload.data === null || !Array.isArray(payload.data.rows)) {
    return null
  }
  return plot as StoredPlotPayload<'diverging-likert'>
}

/**
 * A quick-study rating read as Python stamped it (`quick_study.likert_plot`):
 * read as is. A result stored before the stamp has no field and keeps its
 * browser reader (`DivergingStackedRating` rows, the scalar intent, the NPS
 * bar), named legacy in read-adapters.md.
 */
export const quickStudyDivergingLikertAdapter: PlotReadAdapter<'diverging-likert', StoredQuickStudyLikert> = {
  kind: 'diverging-likert',
  source: { surface: 'quick-study', shape: 'diverging_stacked' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredQuickStudyLikert {
    return typeof stored === 'object' && stored !== null && quickStudyLikertPlotOf((stored as StoredQuickStudyLikert).diverging_likert_plot) !== null
  },
  read(stored: StoredQuickStudyLikert): StoredPlotPayload<'diverging-likert'> {
    return quickStudyLikertPlotOf(stored.diverging_likert_plot) as StoredPlotPayload<'diverging-likert'>
  },
}

/** Every diverging-likert read adapter, the stamped shape first. */
export const DIVERGING_LIKERT_READ_ADAPTERS = [
  deckSlideDivergingLikertAdapter,
  legacyDeckSlideDivergingLikertAdapter,
  quickStudyDivergingLikertAdapter,
] as const

/** The stored slide's plot when Python stamped one, else its legacy read. */
export function readDivergingLikertSlide(slide: StoredRatingLikertSlide): {
  payload: StoredPlotPayload<'diverging-likert'>
  isStamped: boolean
} {
  // `hasStampedPlot`, not the adapter's type guard: the guard would narrow the
  // fallthrough to `never`.
  if (hasStampedPlot(slide)) {
    return { payload: deckSlideDivergingLikertAdapter.read(slide), isStamped: true }
  }
  return { payload: legacyDeckSlideDivergingLikertAdapter.read(slide), isStamped: false }
}

/** What the slide draws: the pooled bar, one bar per row and the legend. */
export interface DivergingLikertSlideBars {
  bar: DivergingLikertDrawnBar
  rows: Array<{ id: string; label: string; imageUrl?: string | null; bar: DivergingLikertDrawnBar; hasNet: boolean }>
  legend: Array<RatingBarLegendEntry>
  netLabel: string | null
  status: 'ok' | 'unsupported'
  /** True when the rows are the stamped plot's (`plot.data.rows`), not the stored slide's. */
  stamped: boolean
}

/**
 * The bars a rating-likert slide draws. A slide with `plot` draws Python's rows
 * (`rows[]` of the slide map one to one onto the plot's rows; a flat slide's
 * plot holds the one pooled bar); a slide without it draws today's resolution.
 */
export function divergingLikertSlideBars(slide: StoredRatingLikertSlide): DivergingLikertSlideBars {
  if (hasStampedPlot(slide)) {
    const data = deckSlideDivergingLikertAdapter.read(slide).data as DivergingLikertStoredData
    const view = divergingLikertActiveView(data)
    const planned = data.rows.map((row) => ({ row, bar: planDivergingLikertRow(row, data, view) }))
    const legend = divergingLikertLegend(data, planned.map((entry) => entry.bar.plan), view)
    const status = data.status === 'unsupported' ? 'unsupported' as const : 'ok' as const
    const unsupportedBar: DivergingLikertDrawnBar = { plan: null, dataStatus: 'unsupported' }
    const flat = !slide.rows?.length
    return {
      bar: planned.length === 1 ? planned[0].bar : status === 'unsupported' ? unsupportedBar : { plan: null, dataStatus: 'empty' },
      rows: flat ? [] : planned.map(({ row, bar }) => ({
        id: row.id,
        label: row.label,
        imageUrl: row.imageUrl ?? null,
        bar,
        hasNet: typeof row.net === 'number',
      })),
      legend,
      netLabel: data.labels.net ?? null,
      status,
      stamped: true,
    }
  }
  const read = resolveLegacyRatingLikert(slide)
  const unsupported = [read.bar, ...read.rows.map((row) => row.bar)]
    .some((bar) => bar.dataStatus === 'unsupported')
  return {
    bar: read.bar,
    rows: read.rows.map((row) => ({ ...row, hasNet: true })),
    legend: read.legend,
    // legacy reader only: a slide without the stamped plot.
    netLabel: slide.displayLabels?.net || 'Net',
    status: unsupported ? 'unsupported' : 'ok',
    stamped: false,
  }
}
